/** Reading an activity event as a sentence, a tone, and somewhere to go.
 *
 * The service answers the feed as typed events with a `data` bag per kind,
 * and says nothing about how to phrase them: the wording is the portal's,
 * as it is for the audit trail. Each kind gets one sentence shape here —
 * who did what to which thing, and the detail that makes it worth reading
 * ("held · the finish line crossings disagree"). A kind this build has never
 * heard of still gets its name and its actor, so the feed never goes blank
 * for something the service was able to record.
 *
 * Tones are the design's and they mean what they mean elsewhere: `warn` is
 * something waiting on a person or gone wrong, `ok` is something that landed,
 * `accent` is a survey arriving, and the rest is `neutral`.
 */

import { DASH } from '@/lib/format';
import type { ActivityEvent, ActivityKind } from '@/lib/types';

export type ActivityTone = 'ok' | 'warn' | 'danger' | 'accent' | 'neutral';

export interface ActivityLine {
  /** Who: a name, or what kind of thing it was. */
  who: string;
  /** What happened, in a few words, present tense avoided. */
  what: string;
  /** The reason, the figure, the note: whatever explains the line. */
  detail: string;
  tone: ActivityTone;
}

export interface ActivityLink {
  label: string;
  /** A portal path, or an external URL for a pull request. */
  to: string;
  external?: boolean;
}

/** The kinds a filter offers, in the order they read best, with the word
 *  the chip wears. */
export const ACTIVITY_FILTERS: ReadonlyArray<{ kind: ActivityKind; label: string }> = [
  { kind: 'job_run', label: 'Job runs' },
  { kind: 'upload_received', label: 'Surveys' },
  { kind: 'upload_status', label: 'Decisions' },
  { kind: 'merge_request_opened', label: 'PRs opened' },
  { kind: 'merge_request_settled', label: 'PRs settled' },
  { kind: 'issue_raised', label: 'Issues' },
  { kind: 'issue_resolved', label: 'Resolutions' },
  { kind: 'track_published', label: 'Published' },
  { kind: 'edit_submitted', label: 'Edits' },
  { kind: 'edit_status', label: 'Edit outcomes' },
];

export function isActivityKind(value: string): value is ActivityKind {
  return ACTIVITY_FILTERS.some((filter) => filter.kind === value);
}

function text(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return String(value);
  }
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** `1 PR`, `3 PRs`. */
function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Who did it, in words. The feed names contributors by their display name
 *  like everybody else; it is an administrator's screen. */
export function activityActor(event: Pick<ActivityEvent, 'actor'>): string {
  const actor = event.actor;
  if (!actor) return 'the service';
  if (actor.name) return actor.name;
  switch (actor.kind) {
    case 'job':
      return 'the merge job';
    case 'system':
      return 'the service';
    case 'contributor':
      return actor.id ?? 'a contributor';
    case 'user':
      return actor.id ?? 'an administrator';
  }
}

/** The status words the decisions use, as a driver reads them. */
function statusWord(status: unknown): string {
  switch (status) {
    case 'pending':
      return 'back to pending';
    case 'held':
      return 'held';
    case 'rejected':
      return 'rejected';
    case 'merged':
      return 'merged';
    default:
      return text(status) || DASH;
  }
}

function mergeStatusWords(status: unknown): string {
  switch (status) {
    case 'auto_merged':
      return 'merged';
    case 'awaiting_review':
      return 'waiting for review';
    case 'ci_failed':
      return 'failed its checks';
    case 'closed':
      return 'closed without merging';
    case 'open':
      return 'open';
    default:
      return text(status) || DASH;
  }
}

/** One line for the feed. */
export function activityLine(event: ActivityEvent): ActivityLine {
  const who = activityActor(event);
  const data = event.data;
  switch (event.kind) {
    case 'job_run': {
      const dispatch = text(data['dispatch']);
      const finished = text(data['finished_at']);
      if (event.actor?.kind === 'user') {
        const scope = text(data['scope']);
        const sent = dispatch === 'sent' || dispatch === '';
        return {
          who,
          what: sent ? 'asked for a job run' : 'asked for a job run that could not be started',
          detail: [scope && scope !== 'all' ? `for ${scope}` : 'for every circuit', sent ? '' : dispatch]
            .filter(Boolean)
            .join(' · '),
          tone: sent ? 'neutral' : 'warn',
        };
      }
      const ok = data['ok'];
      const parts = [
        `${plural(num(data['tracks_touched']) ?? 0, 'circuit')} touched`,
        `${plural(num(data['prs_opened']) ?? 0, 'PR')} opened`,
      ];
      const merged = num(data['auto_merged']) ?? 0;
      const waiting = num(data['awaiting_review']) ?? 0;
      const raised = num(data['issues_raised']) ?? 0;
      if (merged > 0) parts.push(`${merged} merged`);
      if (waiting > 0) parts.push(`${waiting} waiting for review`);
      if (raised > 0) parts.push(`${plural(raised, 'issue')} raised`);
      return {
        who: 'the merge job',
        what: finished ? (ok === 0 ? 'ran and failed' : 'ran') : 'started a run',
        detail: parts.join(' · '),
        tone: ok === 0 ? 'danger' : 'neutral',
      };
    }
    case 'upload_received': {
      const metres = num(data['edges']);
      const runs = num(data['runs']);
      return {
        who,
        what: 'sent a survey',
        detail: [
          metres !== null ? `${metres.toLocaleString()} m of border` : '',
          runs !== null ? plural(runs, 'run') : '',
          text(data['status']) === 'rejected' ? `rejected · ${text(data['status_reason'])}` : '',
        ]
          .filter(Boolean)
          .join(' · '),
        tone: 'accent',
      };
    }
    case 'upload_status': {
      const to = data['to'];
      const count = num(data['count']);
      const reason = text(data['reason']);
      if (count !== null) {
        return {
          who: 'the nightly sweep',
          what: `expired ${plural(count, 'held survey')}`,
          detail: reason,
          tone: 'warn',
        };
      }
      const contributor = text(data['contributor']);
      const via = text(data['via_issue']);
      return {
        who,
        what: `${statusWord(to)} ${contributor ? `${contributor}’s survey` : 'a survey'}`,
        detail: [reason, via ? 'through an issue' : ''].filter(Boolean).join(' · '),
        tone: to === 'rejected' ? 'danger' : to === 'held' ? 'warn' : to === 'merged' ? 'ok' : 'neutral',
      };
    }
    case 'merge_request_opened': {
      const metres = num(data['new_metres']) ?? 0;
      const accounts = num(data['accounts']) ?? 0;
      const status = data['status'];
      return {
        who: 'the merge job',
        what: 'opened a pull request',
        detail: [
          `${metres.toLocaleString()} new m`,
          plural(accounts, 'account'),
          status && status !== 'open' ? `now ${mergeStatusWords(status)}` : '',
        ]
          .filter(Boolean)
          .join(' · '),
        tone: status === 'awaiting_review' || status === 'ci_failed' ? 'warn' : 'neutral',
      };
    }
    case 'merge_request_settled': {
      const to = data['to'];
      const mergedBy = text(data['merged_by']);
      return {
        who: mergedBy || who,
        what:
          to === 'auto_merged'
            ? mergedBy
              ? 'merged the pull request'
              : 'saw the pull request merged'
            : to === 'closed'
              ? 'closed the pull request without merging'
              : `settled the pull request as ${mergeStatusWords(to)}`,
        detail: text(data['pr_state']) ? `${text(data['pr_state'])} on GitHub` : '',
        tone: to === 'auto_merged' ? 'ok' : to === 'closed' ? 'warn' : 'neutral',
      };
    }
    case 'issue_raised': {
      const severity = text(data['severity']);
      return {
        who: 'the service',
        what: `raised a ${text(data['type']).replace(/_/g, ' ')} issue`,
        detail: text(data['summary']),
        tone: severity === 'error' ? 'danger' : 'warn',
      };
    }
    case 'issue_resolved': {
      const status = text(data['status']);
      const decided = text(data['decided_kind']);
      if (decided) {
        return { who, what: `decided the kind is ${decided}`, detail: text(data['summary']), tone: 'ok' };
      }
      const resolution = text(data['resolution']).replace(/_/g, ' ');
      return {
        who,
        what: status === 'ignored' ? 'ignored an issue' : 'resolved an issue',
        detail: [resolution, text(data['summary'])].filter(Boolean).join(' · '),
        tone: status === 'ignored' ? 'neutral' : 'ok',
      };
    }
    case 'track_published': {
      const first = data['first'] === 1 || data['first'] === true;
      const length = num(data['length_m']);
      const left = num(data['coverage_left']);
      const right = num(data['coverage_right']);
      return {
        who: 'the merge job',
        what: first ? 'published the circuit for the first time' : 'republished the circuit',
        detail: [
          length !== null ? `${length.toLocaleString()} m` : '',
          left !== null && right !== null ? `${Math.round(left)} % / ${Math.round(right)} % surveyed` : '',
          data['closed'] === 1 ? 'closes into a lap' : '',
        ]
          .filter(Boolean)
          .join(' · '),
        tone: 'ok',
      };
    }
    case 'edit_submitted': {
      const drawn = num(data['drawn_m']);
      const areas = num(data['excluded_areas']);
      return {
        who,
        what: 'submitted an edit',
        detail: [
          drawn !== null && drawn > 0 ? `${drawn.toLocaleString()} m drawn` : '',
          areas !== null && areas > 0 ? plural(areas, 'area') + ' excluded' : '',
          text(data['note']),
        ]
          .filter(Boolean)
          .join(' · '),
        tone: 'accent',
      };
    }
    case 'edit_status': {
      const status = text(data['status']);
      const words: Record<string, string> = {
        pr_open: 'opened a pull request for the edit',
        applied: 'applied the edit',
        closed: 'closed the edit’s pull request',
        stale: 'found the edit stale',
        failed: 'could not apply the edit',
        superseded: 'superseded the edit',
      };
      return {
        who: 'the merge job',
        what: words[status] ?? `moved the edit to ${status || DASH}`,
        detail: text(data['reason']),
        tone: status === 'applied' ? 'ok' : status === 'failed' ? 'danger' : status === 'pr_open' ? 'neutral' : 'warn',
      };
    }
    default:
      return { who, what: String(event.kind).replace(/_/g, ' '), detail: '', tone: 'neutral' };
  }
}

/** Where a line leads: the thing it is about, and the pull request when
 *  there is one. Each address is one the page at the other end honours: the
 *  Uploads screen filters by circuit, the Issues screen selects an issue by
 *  id within a status, and the merge requests screen takes no filter. */
export function activityLinks(event: ActivityEvent): ActivityLink[] {
  const links: ActivityLink[] = [];
  const ref = event.ref;
  if (ref) {
    switch (ref.type) {
      case 'upload':
        links.push({
          label: 'uploads',
          to: event.official_id
            ? `/admin/uploads?official_id=${encodeURIComponent(event.official_id)}`
            : '/admin/uploads',
        });
        break;
      case 'issue': {
        const status = text(event.data['status']);
        const inList = status === 'resolved' || status === 'ignored' ? `&status=${status}` : '';
        links.push({ label: 'issue', to: `/admin/issues?issue=${encodeURIComponent(ref.id)}${inList}` });
        break;
      }
      case 'merge_request':
      case 'job_run':
        links.push({ label: 'merge requests', to: '/admin/merge' });
        break;
      case 'track_edit':
        links.push({ label: 'editor', to: `/admin/tracks/${encodeURIComponent(event.official_id)}/edit` });
        break;
      default:
        break;
    }
  }
  if (event.pr_url) links.push({ label: 'PR', to: event.pr_url, external: true });
  return links;
}

/** `Tsukuba Circuit`, or the id when the circuit was never named. */
export function activityTrack(event: Pick<ActivityEvent, 'official_id' | 'track_name'>): string {
  return event.track_name || event.official_id;
}
